import React, { createContext, useContext, useState, useEffect } from 'react';

interface Room {
  id: string;
  title: string;
  hostedAt: Date;
  participantCount: number;
}

interface User {
  id: string;
  username: string;
  isVerified: boolean;
  verificationMethod?: 'paid' | 'community' | null;
  roomsHostedThisMonth: Room[];
  profilePicture?: string;
}

interface UserContextType {
  user: User | null;
  setUser: (user: User | null) => void;
  canHostRoom: () => boolean;
  getRemainingRooms: () => number;
  getMaxParticipants: () => number;
  hostRoom: (roomTitle: string) => boolean;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    // Mock user data - in real app this would come from authentication
    const mockUser: User = {
      id: '2',
      username: 'sarah_chen',
      isVerified: true, // Female user is verified
      verificationMethod: 'paid',
      profilePicture: 'https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg?auto=compress&cs=tinysrgb&w=400',
      roomsHostedThisMonth: [
        {
          id: '1',
          title: 'Women in Tech Discussion',
          hostedAt: new Date(),
          participantCount: 45
        },
        {
          id: '2',
          title: 'Creative Writing Workshop',
          hostedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 7), // 1 week ago
          participantCount: 32
        }
      ]
    };
    setUser(mockUser);
  }, []);

  const canHostRoom = (): boolean => {
    if (!user) return false;
    if (user.isVerified) return true; // Unlimited for verified users
    
    // Count rooms hosted this month
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    
    const roomsThisMonth = user.roomsHostedThisMonth.filter(room => {
      const roomDate = new Date(room.hostedAt);
      return roomDate.getMonth() === currentMonth && roomDate.getFullYear() === currentYear;
    });
    
    return roomsThisMonth.length < 2; // 2 rooms per month for unverified
  };

  const getRemainingRooms = (): number => {
    if (!user) return 0;
    if (user.isVerified) return Infinity; // Unlimited for verified users
    
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    
    const roomsThisMonth = user.roomsHostedThisMonth.filter(room => {
      const roomDate = new Date(room.hostedAt);
      return roomDate.getMonth() === currentMonth && roomDate.getFullYear() === currentYear;
    });
    
    return Math.max(0, 2 - roomsThisMonth.length);
  };

  const getMaxParticipants = (): number => {
    if (!user) return 0;
    return user.isVerified ? Infinity : 200; // 200 max for unverified, unlimited for verified
  };

  const hostRoom = (roomTitle: string): boolean => {
    if (!user || !canHostRoom()) return false;
    
    const newRoom: Room = {
      id: Date.now().toString(),
      title: roomTitle,
      hostedAt: new Date(),
      participantCount: 0
    };
    
    const updatedUser = {
      ...user,
      roomsHostedThisMonth: [...user.roomsHostedThisMonth, newRoom]
    };
    
    setUser(updatedUser);
    return true;
  };

  return (
    <UserContext.Provider value={{
      user,
      setUser,
      canHostRoom,
      getRemainingRooms,
      getMaxParticipants,
      hostRoom
    }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}; 